import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Award, CheckCircle2, XCircle, ExternalLink, Play, RotateCcw, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Gauge({ score, pass }) {
  const passed = score >= pass;
  const r = 52, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative h-32 w-32" data-testid="exam-gauge">
      <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={passed ? "#16a34a" : "#0078d4"} strokeWidth="10"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center"><div className="text-3xl font-bold">{score}%</div>
          <div className={`text-xs font-medium ${passed ? "text-green-600" : "text-muted-foreground"}`}>{passed ? "PASS" : `need ${pass}%`}</div></div>
      </div>
    </div>
  );
}

export default function PracticalExam() {
  const [exam, setExam] = useState(null);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/azure/exam"); setExam(data.exam); setStatus(data.status); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/azure/exam/grade");
      setResult(data); setStatus(data.status);
      if (data.passed) toast.success(`Passed with ${data.score}%! Certificate marked with distinction.`);
      else toast.message(`Scored ${data.score}%. ${data.objectives_passed}/${data.objectives_total} objectives complete.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openPortal = () => window.open("/azure", "_blank", "noopener,noreferrer");
  if (!exam) return <div className="p-10 text-muted-foreground">Loading exam…</div>;

  const byId = {}; (result?.objectives || []).forEach((o) => { byId[o.id] = o; });

  return (
    <div className="max-w-4xl mx-auto p-8" data-testid="practical-exam">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#0078d4] grid place-items-center text-white shrink-0"><Award className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display font-extrabold text-2xl tracking-tight">AZ-104 Practical Exam</h1>
          <p className="text-sm text-muted-foreground mt-1">A blank-tenant capstone. Build every required resource inside the <code className="bg-muted px-1 rounded">{exam.resource_group}</code> resource group, then grade yourself. Score {exam.pass_score}%+ to pass with distinction.</p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-6 flex flex-wrap items-center gap-6" data-testid="exam-summary">
        <Gauge score={result?.score ?? status?.best_score ?? 0} pass={exam.pass_score} />
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm text-muted-foreground">Best score</div>
          <div className="text-2xl font-bold">{status?.best_score ?? 0}%</div>
          <div className="text-sm text-muted-foreground mt-2">Attempts: {status?.attempts ?? 0}</div>
          {status?.passed && <div className="mt-2 inline-flex items-center gap-1.5 text-green-600 text-sm font-medium" data-testid="exam-passed-badge"><Trophy className="h-4 w-4" /> Practical exam passed</div>}
        </div>
        <div className="flex flex-col gap-2">
          <Button className="gap-2" variant="outline" onClick={openPortal} data-testid="exam-open-portal"><ExternalLink className="h-4 w-4" /> Open Azure portal</Button>
          <Button className="gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={grade} disabled={busy} data-testid="exam-grade-btn">
            {busy ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {busy ? "Grading…" : "Grade my exam"}
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-2">
        {exam.objectives.map((o, i) => {
          const r = byId[o.id];
          const passed = r?.passed;
          return (
            <div key={o.id} className="rounded-lg border border-border bg-card p-4 flex items-start gap-3" data-testid={`exam-objective-${o.id}`}>
              <div className="mt-0.5">
                {r ? (passed ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-500" />)
                   : <span className="h-5 w-5 rounded-full border-2 border-muted-foreground/40 grid place-items-center text-[10px] font-bold text-muted-foreground">{i + 1}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{o.title}</span>
                  <span className="text-[10px] uppercase tracking-wide bg-muted px-2 py-0.5 rounded">{o.skill}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{o.points} pts</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{o.instruction}</p>
                {r && <p className={`text-xs mt-1 ${passed ? "text-green-600" : "text-red-500"}`}>{r.message}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
